import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { allIndustries } from '../../data/allProjects';
import { useWorkFilters, StatusFilter } from '../../hooks/useWorkFilters';
import { useCursorContext } from '../../hooks/useCursorContext';

export function WorkFilters() {
  const { filters, setIndustry, setTech, setStatus, clearFilters, stats, hasActiveFilters } =
    useWorkFilters();
  const { setCursorState } = useCursorContext();

  const handleHover = (hovering: boolean) => {
    setCursorState(hovering ? 'hovering' : 'default');
  };

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Alle' },
    { value: 'current', label: 'Aktuell' },
    { value: 'completed', label: 'Abgeschlossen' },
  ];

  // Top 8 Technologies
  const topTechnologies = ['AI Agents', 'Swift', 'macOS', 'React', 'Vue.js', 'TypeScript', 'Node.js', 'WordPress'];

  return (
    <div className="py-8 px-8 md:px-12 lg:px-24 border-b border-white/10">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Industry Filters */}
        <div>
          <span className="text-xs text-white/30 tracking-[0.3em] uppercase mb-3 block">
            Branche
          </span>
          <div className="flex flex-wrap gap-2">
            <FilterButton
              active={filters.industry === 'all'}
              onClick={() => setIndustry('all')}
              onHover={handleHover}
            >
              Alle
            </FilterButton>
            {allIndustries.map((industry) => (
              <FilterButton
                key={industry}
                active={filters.industry === industry}
                onClick={() => setIndustry(industry)}
                onHover={handleHover}
              >
                {industry}
              </FilterButton>
            ))}
          </div>
        </div>

        {/* Tech Filters */}
        <div>
          <span className="text-xs text-white/30 tracking-[0.3em] uppercase mb-3 block">
            Technologie
          </span>
          <div className="flex flex-wrap gap-2">
            <FilterButton
              active={filters.tech === 'all'}
              onClick={() => setTech('all')}
              onHover={handleHover}
            >
              Alle
            </FilterButton>
            {topTechnologies.map((tech) => (
              <FilterButton
                key={tech}
                active={filters.tech === tech}
                onClick={() => setTech(tech)}
                onHover={handleHover}
              >
                {tech}
              </FilterButton>
            ))}
          </div>
        </div>

        {/* Status & Results Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
          {/* Status Filters */}
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/30 tracking-[0.3em] uppercase">Status</span>
            <div className="flex gap-2">
              {statusOptions.map((option) => (
                <FilterButton
                  key={option.value}
                  active={filters.status === option.value}
                  onClick={() => setStatus(option.value)}
                  onHover={handleHover}
                  small
                >
                  {option.value === 'current' && (
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2" />
                  )}
                  {option.label}
                </FilterButton>
              ))}
            </div>
          </div>

          {/* Results Count & Clear */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/40">
              {stats.filtered} von {stats.total} Projekten
            </span>
            {hasActiveFilters && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                onClick={clearFilters}
                onMouseEnter={() => handleHover(true)}
                onMouseLeave={() => handleHover(false)}
                className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/60 hover:text-red-400 border border-white/10 hover:border-red-400/50 rounded-full transition-colors"
              >
                <X className="w-3 h-3" />
                Filter zurücksetzen
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  onHover: (hovering: boolean) => void;
  children: React.ReactNode;
  small?: boolean;
}

function FilterButton({ active, onClick, onHover, children, small }: FilterButtonProps) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      className={`inline-flex items-center ${
        small ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'
      } rounded-full transition-colors ${
        active
          ? 'bg-emerald-500 text-black'
          : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
      }`}
    >
      {children}
    </motion.button>
  );
}
